/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { FormikHelpers, useFormik } from 'formik';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';

import { Button, Typography } from '@mui/material';

import { OverlayFormWithControls } from '../../components/OverlayFormWithControls/OverlayFormWithControls';
import { StepsIndicator } from '../../components/StepsIndicator/StepsIndicator';
import { Namespaces } from '../../i18n';
import { OrganizationApiService } from '../../services/OrganizationApiService';
import { ApiClientError } from '../../types/api/ApiClientError';
import { DataState } from '../../types/common/DataState';
import { CreateOrganizationRequest } from '../../types/organization/CreateOrganizationRequest';
import { dataError, dataLoading, isDataLoading } from '../../utils/dataState';
import { unprocessibleEntityToObject } from '../../utils/unprocessibleEntityToObject';
import { useObjectState } from '../../utils/useObjectState';
import { CreateOrganizationStep1 } from './components/CreateOrganizationStep1';
import { CreateOrganizationStep2 } from './components/CreateOrganizationStep2';
import { getInitialState, step1ValidationSchema, step2ValidationSchema } from './CreateOrganizationOverlay.consts';
import { useCreateOrganizationOverlayFormStyles } from './CreateOrganizationOverlay.styles';
import {
  CreateOrganizationApiValidationErrors,
  CreateOrganizationValues,
  ResetApiErrorForField,
} from './CreateOrganizationOverlay.types';

const connector = connect();
type PropsFromRedux = ConnectedProps<typeof connector>;
type CreateOrganizationOverlayFormProps = PropsFromRedux & {
  onCreateOrganization: () => any;
};

type State = {
  step: number;
  dataState: DataState;
  errorMessage?: string;
  apiErrors: CreateOrganizationApiValidationErrors;
};

const CreateOrganizationOverlayFormComponent = memo<CreateOrganizationOverlayFormProps>(props => {
  const { onCreateOrganization } = props;
  const classes = useCreateOrganizationOverlayFormStyles();
  const { t } = useTranslation([Namespaces.CreateOrganization, Namespaces.Common]);
  const [state, setState] = useObjectState<State>({
    step: 1,
    dataState: DataState.notAsked,
    apiErrors: {},
  });
  const { step, dataState, apiErrors } = state;

  const initialValues: CreateOrganizationValues = useMemo(() => getInitialState(), []);

  const validationSchema = useMemo(() => {
    return step === 1 ? step1ValidationSchema : step2ValidationSchema;
  }, [step]);

  const changeStep = useCallback(
    (nextStep: number) => {
      setState({ step: nextStep });
    },
    [setState],
  );

  const onFormComplete = useCallback(
    (values: CreateOrganizationValues) => {
      setState({
        dataState: dataLoading(),
        errorMessage: undefined,
        apiErrors: {},
      });
      const createOrganization = async () => {
        try {
          const data: CreateOrganizationRequest = {
            name: values.name.trim(),
            description: values.description.trim() || undefined,
            email: values.email.trim() || undefined,
            companyName: values.companyName.trim(),
            language: values.language.trim(),
            vatNumber: values.vatNumber.trim() || undefined,
            address: {
              line1: values.address.line1.trim(),
              line2: values.address.line2?.trim() || undefined,
              line3: values.address.line3?.trim() || undefined,
              postalCode: values.address.postalCode.trim(),
              city: values.address.city.trim(),
              state: values.address.state.trim() || undefined,
              countryCode: values.address.countryCode,
            },
          };
          const { thumbnail } = values;
          const result = await OrganizationApiService.createOrganization(data);
          if (thumbnail) {
            await OrganizationApiService.updateOrganizationThumbnail(
              result.data.payload.id,
              thumbnail.type === 'base64'
                ? { type: 'base64', base64: thumbnail.base64 }
                : { type: 'seed', seed: thumbnail.seed },
            );
          }
          onCreateOrganization();
        } catch (e) {
          const error = e as ApiClientError;
          const responseData = error.response?.data;
          if (!responseData) {
            setState({ dataState: dataError(), errorMessage: undefined });
          } else {
            const errorMessage = responseData.message;
            if (errorMessage === 'unprocessable_entity') {
              setState({
                dataState: dataError(),
                apiErrors: unprocessibleEntityToObject(responseData.payload),
              });
            } else {
              setState({ dataState: dataError(), errorMessage });
            }
          }
        }
      };
      void createOrganization();
    },
    [setState, onCreateOrganization],
  );

  const onSubmit = useCallback(
    (values: CreateOrganizationValues, { setTouched }: FormikHelpers<CreateOrganizationValues>) => {
      if (step === 1) {
        void setTouched({});
        changeStep(2);
      } else if (step === 2) {
        onFormComplete(values);
      }
    },
    [changeStep, onFormComplete, step],
  );

  const formProps = useFormik<CreateOrganizationValues>({
    onSubmit: onSubmit,
    initialValues: initialValues,
    validationSchema: validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
  });

  const { setTouched } = formProps;

  const onBackClick = useCallback(() => {
    void setTouched({});
    if (step <= 2) {
      changeStep(1);
    } else {
      changeStep(step - 1);
    }
  }, [changeStep, step, setTouched]);

  const isFetching = isDataLoading(dataState);

  const resetApiError: ResetApiErrorForField = useCallback(
    name => {
      if (apiErrors[name]) {
        const nextErrors = { ...apiErrors };
        delete nextErrors[name];
        setState({ apiErrors: nextErrors });
      }
    },
    [apiErrors, setState],
  );

  return (
    <div className={classes.root}>
      {
        <>
          <Typography classes={{ h2: classes.title }} variant="h2">
            {t('labels.new_organization')}
          </Typography>
          <OverlayFormWithControls
            onSubmit={formProps.handleSubmit}
            stepsIndicator={<StepsIndicator stepsCount={2} stepNumber={step} />}
            buttons={
              <>
                {step > 1 && (
                  <Button onClick={onBackClick} variant="outlined" disabled={formProps.isValidating || isFetching}>
                    {t('buttons.previous')}
                  </Button>
                )}
                {step < 2 && (
                  <Button
                    onClick={formProps.submitForm}
                    variant="contained"
                    disabled={formProps.isValidating || isFetching}
                  >
                    {t('buttons.next')}
                  </Button>
                )}
                {step === 2 && (
                  <Button
                    onClick={formProps.submitForm}
                    variant="contained"
                    disabled={formProps.isValidating || isFetching}
                  >
                    {t('buttons.create')}
                  </Button>
                )}
              </>
            }
          >
            {step === 1 && (
              <CreateOrganizationStep1 {...formProps} resetApiError={resetApiError} apiErrors={apiErrors} />
            )}
            {step === 2 && (
              <CreateOrganizationStep2 {...formProps} resetApiError={resetApiError} apiErrors={apiErrors} />
            )}
          </OverlayFormWithControls>
        </>
      }
    </div>
  );
});

export const CreateOrganizationOverlayForm = connector(CreateOrganizationOverlayFormComponent);
